'use client';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { resetEquipmentDataState } from '../equipmentDataSlice';
import { resetReception } from '@/features/app/reception/receptionSlice';
import { useAppDispatch, useAppSelector } from '@/hooks/lib/customStore';
import { useMemo } from 'react';
import {
  selectActiveDevice,
  makeSelectDevicePhotos,
  selectEmptyDevicePhotos,
} from '@/helpers/app/reception';
import { removePhoto as removeDraftPhoto } from '@/features/app/receptionWizardSlice';
import usePageNavigation from '@/hooks/usePageNavigation';
import { useTranslation } from 'react-i18next';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // onDiscard: () => void;
  onStay?: () => void;
  // Ruta a la que se redirige tras descartar. Por defecto 'client'
  redirectTo?: string;
  textRedirectTo?: string;
};

export default function DiscardMultiDeviceDialog({
  open,
  onOpenChange,
  onStay,
  redirectTo,
  textRedirectTo,
}: Props) {
  const dispatch = useAppDispatch();
  const activeDevice = useAppSelector(selectActiveDevice);
  const photosSelector = useMemo(() => {
    if (!activeDevice) {
      return selectEmptyDevicePhotos;
    }
    return makeSelectDevicePhotos(activeDevice.id);
  }, [activeDevice?.id]);
  const photos = useAppSelector(photosSelector); // Fotos del dispositivo en edición para limpiar correctamente.
  const { navigateNextPage } = usePageNavigation();
  const { t } = useTranslation();

  const onDiscard = () => {
    photos.forEach((photo) => dispatch(removeDraftPhoto(photo.id)));
    dispatch(resetEquipmentDataState());
    dispatch(resetReception());
    navigateNextPage((redirectTo ?? 'client') as string);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-2xl">{t('DiscardDialog.title')}</DialogTitle>
          <DialogDescription>{t('DiscardDialog.description')}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            onClick={onDiscard}
            className="bg-gray-300 h-11 text-black font-bold text-lg hover:bg-gray-400/60 focus-visible:ring-0 focus-visible:outline-none transition"
          >
            {t('DiscardDialog.btn-discard')} {textRedirectTo}
          </Button>
          <Button
            type="button"
            onClick={onStay ?? (() => onOpenChange(false))}
            className="h-11
                w-ful
                bg-primary text-primary-foreground
                transition
                hover:brightness-95
                text-lg font-bold
                active:bg-primary/65 focus:bg-primary/65
                focus-visible:ring-0 focus-visible:outline-none focus-visible:outline-offset-2"
          >
            {t('DiscardDialog.btn-continue')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
